/**
 * useEngagementNudges.js — one queue for the install card, the two platform
 * coaches and the push soft-ask (PWA install / notifications audit 2026-11-03,
 * Section 3.3).
 *
 * Before this module the four nudges each had their own timer and no queue:
 * at registration the push dialog opened on mount and the install card slid
 * up five seconds later, over it. Now every nudge is a candidate in ONE FIFO
 * queue and only one overlay is ever open:
 *
 *   enqueueNudge({ kind, moment?, reason, onceKey? })
 *     kind    'coach-huawei' | 'coach-ios' | 'install' | 'push'
 *     moment  the push priming moment ('orderbox', 'deposit', ...) — push only
 *     reason  who asked: 'boot', 'tap', 'session', 'launch', 'value-moment',
 *             'first-browse', 'day-open', 'bus'
 *     onceKey a localStorage key: written when the nudge is SHOWN, and a
 *             candidate carrying a key already written is dropped (the
 *             collector's offer, once per plan)
 *
 * Candidates queued together run coach → install → push; within a kind, first
 * in, first out. A candidate identical to one pending or open is ignored.
 *
 * When a candidate reaches the head it is asked "may I show now":
 *   - Telegram (installChannel() === 'tma')             → dropped, every kind
 *   - the auth state its kind needs (install and push
 *     need a signed-in user; the coaches none)          → dropped
 *   - the club policy, engagementFor(): install.trigger,
 *     install.channels, pushMoments                     → dropped
 *   - the collector's day: only the install at day open,
 *     on the day page itself (never a shop page)       → dropped
 *   - the per-user state: `pwa_install_snoozed_until`, `onceKey`,
 *     the Huawei coach's dismissed key, the iOS coach's
 *     snooze; push asks pushPreflightDecision inside
 *     its presenter, exactly as before                  → dropped
 *   - isReady false, a reload hold, an open F7 modal,
 *     the router mid-transition                         → deferred, retried
 * A dropped install comes back on the next moment that requests it when the
 * club's `install.reaskOnNewMoment` is true. On Android an install also waits
 * for Chrome's `beforeinstallprompt` (captured as `window.promptEvent` in
 * src/index.html): without it the card has no one-tap install, so the offer
 * is parked and queued again behind the next push nudge, or replaced by the
 * next install request, instead of opening empty. Samsung Internet and the
 * other Android browsers wait for no event: the install there is usePwaInstall's
 * "install in Chrome" sheet (a launch token into Chrome).
 *
 * A nudge completes when its sheet or dialog closes. The scheduler holds
 * `holdReload('nudge')` while one is open, so the service-worker update dialog
 * — which is NOT a nudge and keeps its own path in App.vue — waits for it; the
 * scheduler in turn waits while the update dialog is open (it is a `.modal-in`).
 *
 * The seller club's install trigger is 'after-value-moment': every push nudge
 * that completes queues an install behind it, and a later session's first boot
 * may offer it once a push nudge was answered in an earlier one.
 *
 * iPhone (decision D7, owner research 2026-11-03): since iOS 17.2 Safari copies
 * the site's cookies into a new Home Screen app once, at install, so a member
 * who signs in first opens the installed app signed in; one who installs first
 * must sign in again inside it, where the Telegram redirect leaves the app. So
 * on 'ios-safari' the install nudge itself — same gates, same slot, same club
 * triggers, signed in — is shown as the iOS coach, and the coach never opens
 * on the login screen. Its close is a snooze of PWA_SNOOZE_DAYS
 * (`poolit:ios-install-coach-snoozed-until`), written here. The one iOS nudge
 * allowed before sign-in is the in-app-browser variant ('ios-webview', "open
 * this in Safari"): cookies set inside an in-app view stay there, so the
 * escape must come first. A tap on the profile row opens the coach regardless.
 */

import { shallowRef, watch } from 'vue';
import { engagementFor } from '@/clubs/manifest.js';
import { installChannel, isAndroidNonChrome, isHuaweiAndroid } from '@/platform/capabilities.js';
import { useAuthStore } from '@/stores/index.js';

// Queue order when several candidates wait at once.
const KIND_RANK = Object.freeze({ 'coach-huawei': 0, 'coach-ios': 0, install: 1, push: 2 });

/** Quiet days after the install card or the iOS coach is dismissed. */
export const PWA_SNOOZE_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

// The per-user state, read here. The Huawei coach writes its dismissed key and
// usePwaInstall the card's snooze; the iOS coach's snooze is written here, on
// its close.
const HUAWEI_COACH_DISMISSED_KEY = 'poolit:huawei-coach-dismissed-v2';
const INSTALL_SNOOZE_KEY = 'pwa_install_snoozed_until';
const IOS_COACH_SNOOZE_KEY = 'poolit:ios-install-coach-snoozed-until';
// The iOS coach's old forever dismissal: read once, as a snooze from that day.
const IOS_COACH_DISMISSED_KEY = 'poolit:ios-install-coach-dismissed';

// Which club install trigger each install reason answers to.
const INSTALL_REASON_TRIGGER = Object.freeze({
  'value-moment': 'after-value-moment',
  session: 'after-value-moment',
  launch: 'after-value-moment',
  'first-browse': 'after-first-browse',
  'day-open': 'at-day-open',
});

// A boot-time reason may offer install to a seller only once a push nudge was
// answered in an earlier session ("the first boot of the next session").
const BOOT_INSTALL_REASONS = new Set(['session', 'launch']);
const ANSWERED_PRIMING = new Set(['granted', 'soft-declined', 'denied']);

const RETRY_MS = 500;
// The longest an install or push ask may wait in the queue for its turn.
const STALE_MS = 60_000;

// shallowRef: `finish` compares the open candidate by identity, which a deep
// ref's proxy would break.
const current = shallowRef(null);
let queue = [];
let options = null;
let releaseHold = null;
let retryTimer = null;
let installAvailable = false;
let installSettled = false;
// An Android install that came up before Chrome's beforeinstallprompt: parked,
// and queued again behind the next push nudge (the next moment).
let parkedInstall = null;

const SHOW = 'show';
const DROP = 'drop';
const DEFER = 'defer';

// Identical = same kind and, for push, the same moment; for install, the same
// reason and once-key (a deferred boot request must not swallow the
// supplier's one first-browse request — they answer to different club
// triggers — and one plan's offer must not swallow the next plan's).
function sameCandidate(a, b) {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'push') return a.moment === b.moment;
  // Two plans' offers are two offers.
  if (a.kind === 'install') return a.reason === b.reason && a.onceKey === b.onceKey;
  return true;
}

function readFlag(key) {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    // Blocked storage reads as dismissed, as the coaches always did.
    return true;
  }
}

// A snooze runs until `until`, but never longer than PWA_SNOOZE_DAYS from now:
// a value written with the clock ahead (or corrupted) must not silence an
// offer for months. NaN reads as not snoozed and the next close rewrites it.
function snoozedUntil(raw) {
  const until = Number(raw);
  const now = Date.now();
  return now < until && until - now <= PWA_SNOOZE_DAYS * DAY_MS;
}

function installSnoozed() {
  try {
    const until = localStorage.getItem(INSTALL_SNOOZE_KEY);
    return until ? snoozedUntil(until) : false;
  } catch {
    // Blocked storage: the card's own snooze write is lost too, so reading it
    // as snoozed is what keeps the card from coming back at every moment.
    return true;
  }
}

function snoozeIosCoach() {
  try {
    localStorage.setItem(IOS_COACH_SNOOZE_KEY, String(Date.now() + PWA_SNOOZE_DAYS * DAY_MS));
  } catch {
    // Storage blocked — it reads as snoozed anyway.
  }
}

function iosCoachSnoozed() {
  try {
    const until = localStorage.getItem(IOS_COACH_SNOOZE_KEY);
    if (until !== null) return snoozedUntil(until);
    // Dismissed forever before the snooze existed: snoozed from now, once —
    // the new key is never removed, so this runs a single time.
    if (localStorage.getItem(IOS_COACH_DISMISSED_KEY) === '1') {
      snoozeIosCoach();
      return true;
    }
    return false;
  } catch {
    // Blocked storage reads as dismissed, as the coaches always did.
    return true;
  }
}

function policy(auth) {
  return engagementFor({
    supplierStage: auth.supplierStage,
    isSupplier: auth.isSupplier,
    isCollector: auth.isCollector,
    hasActiveMembership: auth.hasActiveMembership,
  });
}

// F7 keeps no open-modal counter; every open dialog, sheet and popup carries
// `modal-in` until its close starts. A toast is a Modal too, but it is not an
// overlay and closes itself, so it does not hold the queue.
function hasOpenModal() {
  if (typeof document === 'undefined') return false;
  return document.querySelector('.modal-in:not(.toast)') !== null;
}

// The collector's day (/collector/…), asked through App.vue's collector/session
// helper: importing it here would pull the platform adapter into every page
// that imports the scheduler.
function onCollectorDay() {
  return options.isCollectorPath() === true;
}

// The day page itself (/collector/), not a shop page under it.
function onCollectorDayPage() {
  return options.isCollectorDayPage?.() === true;
}

function routerMoving() {
  const router = typeof window !== 'undefined' ? window.f7Instance?.views?.main?.router : null;
  return Boolean(router && router.allowPageChange === false);
}

function installDecision(candidate, auth, channel) {
  const club = policy(auth);
  if (installSettled) return { action: DROP, reason: 'settled' };
  // Rule 6 of usePwaInstall: on the collector's day only through its own
  // trigger — the day page once the morning download is on the phone
  // (CollectorDayPage) — never on a shop page, where the card would slide
  // over «خلصت المحل» and each sheet's own action.
  if (onCollectorDay() && !(candidate.reason === 'day-open' && onCollectorDayPage())) {
    return { action: DROP, reason: 'collector-path' };
  }
  // …and that trigger is the day page's alone: a day-open offer still queued
  // when he left the collector's screens altogether does not follow him.
  if (candidate.reason === 'day-open' && !onCollectorDayPage()) {
    return { action: DROP, reason: 'off-day-page' };
  }
  if (INSTALL_REASON_TRIGGER[candidate.reason] !== club.install.trigger) {
    return { action: DROP, reason: 'trigger' };
  }
  if (!club.install.channels.includes(channel)) return { action: DROP, reason: 'channel' };
  // On iPhone Safari the install nudge is the iOS coach, with its own snooze.
  const viaIosCoach = channel === 'ios-safari';
  if (viaIosCoach ? iosCoachSnoozed() : installSnoozed())
    return { action: DROP, reason: 'snoozed' };
  if (candidate.onceKey && readFlag(candidate.onceKey)) return { action: DROP, reason: 'offered' };
  if (club.install.trigger === 'after-value-moment' && BOOT_INSTALL_REASONS.has(candidate.reason)) {
    const status = options.primingStatus?.(auth.user?.id ?? null);
    if (!ANSWERED_PRIMING.has(status)) return { action: DROP, reason: 'no-value-moment-yet' };
  }
  // Samsung Internet and the other Android browsers (isAndroidNonChrome) never
  // bring an install event worth waiting for: there the install IS the
  // handoff to Chrome with a launch token (usePwaInstall's sheet), which needs
  // no beforeinstallprompt — and the member is signed in (needsAuth above).
  if (
    channel === 'android-prompt' &&
    !isAndroidNonChrome() &&
    !installAvailable &&
    !(typeof window !== 'undefined' && window.promptEvent)
  ) {
    return { action: DROP, reason: 'awaiting-beforeinstallprompt' };
  }
  return viaIosCoach
    ? { action: SHOW, reason: 'ok', present: 'coach-ios' }
    : { action: SHOW, reason: 'ok' };
}

function iosCoachDecision(candidate, channel) {
  // A tap on the profile row opens it whatever the snooze says.
  if (candidate.reason === 'tap' && (channel === 'ios-safari' || channel === 'ios-webview')) {
    return { action: SHOW, reason: 'tap' };
  }
  // On Safari it comes only through the install slot, after sign-in.
  if (channel === 'ios-safari') return { action: DROP, reason: 'install-slot' };
  if (channel !== 'ios-webview') return { action: DROP, reason: 'device' };
  if (iosCoachSnoozed()) return { action: DROP, reason: 'snoozed' };
  if (onCollectorDay()) return { action: DROP, reason: 'collector-path' };
  return { action: SHOW, reason: 'ok' };
}

/**
 * May this candidate show now? Reads the state it names; its one write is the
 * old iOS dismissal turned into a snooze, once (iosCoachSnoozed).
 */
function decide(candidate) {
  if (!options.isReady.value) return { action: DEFER, reason: 'not-ready' };

  const channel = installChannel();
  if (channel === 'tma') return { action: DROP, reason: 'tma' };

  const auth = useAuthStore();
  const needsAuth = candidate.kind === 'install' || candidate.kind === 'push';
  if (needsAuth && !auth.isAuthenticated) return { action: DROP, reason: 'auth' };

  let verdict = { action: SHOW, reason: 'ok' };
  if (candidate.kind === 'coach-huawei') {
    if (!isHuaweiAndroid()) verdict = { action: DROP, reason: 'device' };
    else if (readFlag(HUAWEI_COACH_DISMISSED_KEY)) verdict = { action: DROP, reason: 'dismissed' };
    else if (onCollectorDay()) verdict = { action: DROP, reason: 'collector-path' };
  } else if (candidate.kind === 'coach-ios') {
    verdict = iosCoachDecision(candidate, channel);
  } else if (candidate.kind === 'install') {
    verdict = installDecision(candidate, auth, channel);
  } else if (candidate.kind === 'push') {
    if (!policy(auth).pushMoments.includes(candidate.moment)) {
      verdict = { action: DROP, reason: 'moment' };
    } else if (onCollectorDay()) verdict = { action: DROP, reason: 'collector-path' };
  }
  // An ask waiting too long behind another overlay has lost its moment.
  if (
    verdict.action !== DROP &&
    (candidate.kind === 'push' || candidate.kind === 'install') &&
    Date.now() - candidate.queuedAt > STALE_MS
  ) {
    verdict = { action: DROP, reason: 'stale' };
  }
  if (verdict.action === DROP) return verdict;

  if (options.isReloadBusy.value) return { action: DEFER, reason: 'reload-busy' };
  if (hasOpenModal()) return { action: DEFER, reason: 'modal-open' };
  if (routerMoving()) return { action: DEFER, reason: 'router-moving' };
  return verdict;
}

function scheduleRetry() {
  if (retryTimer) return;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    pump();
  }, RETRY_MS);
}

// A drop is never the user's decision (a trigger of another club, a snooze, a
// channel), so it never settles install for the session; only a shown offer
// does (`finish`). A missing beforeinstallprompt parks the offer for the next moment.
function onDropped(candidate, reason) {
  if (candidate.kind === 'install' && reason === 'awaiting-beforeinstallprompt') {
    parkedInstall = candidate;
  }
}

function finish(candidate) {
  if (current.value !== candidate) return;
  current.value = null;
  if (releaseHold) {
    releaseHold();
    releaseHold = null;
  }
  // The iOS coach's close is always a dismissal (both its buttons): a snooze.
  if (candidate.kind === 'coach-ios') snoozeIosCoach();
  // A nudge its presenter did not show after all (the card failed to load, it
  // was put back, the presenter threw) was never seen: its once-key is taken
  // back, so the collector's offer for the plan is not spent on nothing.
  if (candidate.onceKey && (candidate.unshown || candidate.requeued || candidate.failed)) {
    try {
      localStorage.removeItem(candidate.onceKey);
    } catch {
      // Storage blocked: it reads as offered anyway.
    }
  }
  if (candidate.kind === 'install' || candidate.slot === 'install') {
    const auth = useAuthStore();
    if (auth.isAuthenticated && !policy(auth).install.reaskOnNewMoment) installSettled = true;
  }
  // The next nudge waits one task: the reload hold just dropped to 0, and the
  // service-worker update dialog (which waits for it) must get its turn before
  // the next nudge takes the hold again. It also lets a coach shown twice in a
  // row see its `opened` go false, then true.
  setTimeout(() => {
    // A push nudge answered is the seller's value moment: the install card is
    // next. Any club's parked install comes back at the same point.
    if (candidate.kind === 'push' && !candidate.requeued) {
      const auth = useAuthStore();
      if (parkedInstall) {
        const parked = parkedInstall;
        parkedInstall = null;
        enqueueNudge(parked);
      }
      if (auth.isAuthenticated && policy(auth).install.trigger === 'after-value-moment') {
        enqueueNudge({ kind: 'install', reason: 'value-moment' });
      }
    }
    pump();
  }, 0);
}

function show(candidate) {
  current.value = candidate;
  releaseHold = options.holdReload('nudge');
  if (candidate.onceKey) {
    try {
      localStorage.setItem(candidate.onceKey, '1');
    } catch {
      // Blocked storage reads as offered anyway (readFlag).
    }
  }
  // The coaches are rendered by App.vue from `current` and report back through
  // completeNudge(); the dialogs and the install card resolve when they close.
  if (candidate.kind === 'coach-huawei' || candidate.kind === 'coach-ios') return;
  const present = options.presenters?.[candidate.kind];
  if (typeof present !== 'function') {
    finish(candidate);
    return;
  }
  // A presenter may resolve 'requeued': it did not show after all and put the
  // candidate back, so its close is not an answered moment; or 'unshown': it
  // could not open at all.
  Promise.resolve()
    .then(() => present(candidate))
    .then((outcome) => {
      if (outcome === 'requeued') candidate.requeued = true;
      if (outcome === 'unshown') candidate.unshown = true;
      return null;
    })
    .catch((err) => {
      candidate.failed = true;
      console.warn(`[nudges] ${candidate.kind} presenter failed:`, err);
    })
    .finally(() => finish(candidate))
    .catch(() => {
      /* finish never throws; the chain is best-effort */
    });
}

function pump() {
  if (!options || current.value) return;
  while (queue.length > 0) {
    const [head] = queue;
    const verdict = decide(head);
    if (verdict.action === DEFER) {
      scheduleRetry();
      return;
    }
    queue.shift();
    if (verdict.action === DROP) {
      onDropped(head, verdict.reason);
      continue;
    }
    // An install shown as the iOS coach keeps its install bookkeeping.
    show(verdict.present ? { ...head, kind: verdict.present, slot: head.kind } : head);
    return;
  }
}

/**
 * Queue a nudge. Safe to call before App.vue has set the scheduler up — the
 * queue waits for it. Returns false when the candidate was ignored (unknown
 * kind, Telegram, or identical to one pending or open).
 *
 * @param {{ kind: 'coach-huawei'|'coach-ios'|'install'|'push', moment?: string, reason?: string, onceKey?: string }} candidate
 * @returns {boolean}
 */
export function enqueueNudge(candidate) {
  if (!candidate || KIND_RANK[candidate.kind] === undefined) return false;
  // A fresh install request supersedes the parked one.
  if (candidate.kind === 'install') parkedInstall = null;
  if (candidate.kind === 'push' && (typeof candidate.moment !== 'string' || !candidate.moment))
    return false;
  if (installChannel() === 'tma') return false;
  const next = {
    kind: candidate.kind,
    moment: candidate.moment,
    reason: candidate.reason ?? 'unspecified',
    queuedAt: Date.now(),
  };
  if (typeof candidate.onceKey === 'string' && candidate.onceKey) next.onceKey = candidate.onceKey;
  if (current.value && sameCandidate(current.value, next)) return false;
  if (queue.some((pending) => sameCandidate(pending, next))) return false;
  const rank = KIND_RANK[next.kind];
  const at = queue.findIndex((pending) => KIND_RANK[pending.kind] > rank);
  if (at === -1) queue.push(next);
  else queue.splice(at, 0, next);
  pump();
  return true;
}

/** The open coach (or any nudge) closed: complete it and show the next. */
export function completeNudge(kind) {
  if (current.value && current.value.kind === kind) finish(current.value);
}

/**
 * Chrome's install event reached the card (`pwa-install-available-event`):
 * the next Android install candidate may show. `false` once that event was
 * used (the native prompt accepted): Chrome gives one per page load.
 */
export function markInstallAvailable(available = true) {
  installAvailable = available;
  if (available) unparkInstall();
}

// Chrome's install event arrived after an Android install had to wait for it:
// the parked offer comes back now if its moment is still fresh. Without this
// the collector's day-open offer (no push nudge there to bring it back) would
// be lost whenever the morning download finished first.
function unparkInstall() {
  if (!parkedInstall || Date.now() - parkedInstall.queuedAt > STALE_MS) return;
  const parked = parkedInstall;
  parkedInstall = null;
  enqueueNudge(parked);
}

// src/index.html captures `beforeinstallprompt` as `window.promptEvent` with a
// listener on window that calls stopImmediatePropagation, so a later bubbling
// listener on window would never run: this one listens in the capture phase,
// which Chrome runs first at the target, and looks one task later, once
// index.html's listener has stored the event.
function onBeforeInstallPrompt() {
  setTimeout(unparkInstall, 0);
}

/**
 * Set the scheduler up. Call ONCE from App.vue <script setup>.
 *
 * @param {object} config
 * @param {import('vue').Ref<boolean>} config.isReady       App.vue's boot flag.
 * @param {import('vue').Ref<boolean>} config.isReloadBusy  usePwa().isReloadBusy.
 * @param {(reason: string) => () => void} config.holdReload usePwa().holdReload.
 * @param {() => boolean} config.isCollectorPath collector/session.js isCollectorPath.
 * @param {() => boolean} [config.isCollectorDayPage] the day page itself (/collector/).
 * @param {(userId: string|number|null) => string|undefined} config.primingStatus
 *   the user's `poolit:push-priming-state` status (usePushNotifications'
 *   getPrimingState). Passed in so importing the scheduler stays light: it is
 *   imported by every page that asks for a nudge.
 * @param {{ push?: Function, install?: Function }} config.presenters
 *   each `(candidate) => Promise` that resolves when its overlay has closed.
 * @returns {{ current: import('vue').Ref<object|null>, enqueueNudge: Function, completeNudge: Function }}
 */
let stopWatches = [];

// Another user (logout, a 401, act-as, a login on the same tab): the pending
// install and push asks were for the previous one, and so was the session's
// install bookkeeping. The coaches are per device and stay.
function forgetUser() {
  queue = queue.filter((pending) => pending.kind !== 'install' && pending.kind !== 'push');
  parkedInstall = null;
  installSettled = false;
}

export function useEngagementNudges(config) {
  // A second setup (hot reload of App.vue) replaces the first, never doubles it.
  stopWatches.forEach((stop) => stop());
  options = config;
  stopWatches = [
    watch(config.isReady, (ready) => {
      if (ready) pump();
    }),
    // One task later, like `finish`: the update dialog's own watcher on the
    // same flag opens first, and the next decision sees its `.modal-in`.
    watch(config.isReloadBusy, (busy) => {
      if (!busy) setTimeout(pump, 0);
    }),
    watch(
      () => useAuthStore().user?.id ?? null,
      (id, previous) => {
        if (id !== previous) forgetUser();
      },
    ),
  ];
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt, { capture: true });
    stopWatches.push(() =>
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt, { capture: true }),
    );
  }
  pump();
  return { current, enqueueNudge, completeNudge };
}

/** Test seam: forget every candidate, flag and option. */
export function __resetEngagementNudgesForTests() {
  stopWatches.forEach((stop) => stop());
  stopWatches = [];
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  if (releaseHold) releaseHold();
  releaseHold = null;
  queue = [];
  options = null;
  current.value = null;
  installAvailable = false;
  installSettled = false;
  parkedInstall = null;
}
